// "See copies" popover — shows every campaign that was copied (forked) from
// the current one, ranked by book rate, so staff/partners can spot which
// spin-off is performing best. Renders as a compact button next to the other
// campaign header actions; opens a popover with the ranked list.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GitBranch, Loader2, ChevronRight, Copy } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";

/**
 * @param {object} campaign - the current (source) campaign document
 */
export function SeeCopiesButton({ campaign }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [copies, setCopies] = useState(null);   // null = never loaded

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/outbound/campaigns/${campaign.campaign_id}/copies`);
      setCopies(data.copies || []);
    } catch (e) {
      setCopies([]);
      // Non-fatal — the button still shows a "no copies" empty state below.
      console.warn("Failed to load campaign copies:", formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  // Lazy-load once when the popover first opens.
  useEffect(() => {
    if (open && copies === null) load();
  }, [open]);

  const total = copies?.length ?? null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="ai-outbound-see-copies-btn"
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 shadow-sm transition-colors hover:bg-slate-50 hover:border-slate-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
          title="Show every campaign copied from this one, ranked by book rate"
        >
          <GitBranch className="h-3.5 w-3.5" />
          See copies
          {total !== null && (
            <span className={`ml-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
              total > 0
                ? "bg-blue-100 text-blue-700"
                : "bg-slate-100 text-slate-500"
            }`}>{total}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0" data-testid="ai-outbound-see-copies-popover">
        <div className="border-b border-slate-100 px-3 py-2">
          <div className="flex items-center gap-2">
            <Copy className="h-3.5 w-3.5 text-primary" />
            <div className="font-semibold text-slate-800 text-sm">Campaigns copied from this one</div>
          </div>
          <div className="mt-0.5 text-[11px] text-slate-400">
            Ranked by book rate (best first). Click any row to open it.
          </div>
        </div>

        {loading && (
          <div className="flex items-center gap-2 px-3 py-6 text-sm text-slate-400">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading copies…
          </div>
        )}

        {!loading && copies?.length === 0 && (
          <div className="px-4 py-8 text-center">
            <div className="text-xs font-semibold text-slate-600">No copies yet</div>
            <div className="mt-1 text-[11px] text-slate-500">
              Click <strong>Copy to org</strong> above to spin off a variant into another
              organization&rsquo;s namespace. It&rsquo;ll appear here so you can compare
              its performance to this parent campaign.
            </div>
          </div>
        )}

        {!loading && copies && copies.length > 0 && (
          <ul className="max-h-80 overflow-y-auto divide-y divide-slate-100"
              data-testid="ai-outbound-see-copies-list">
            {copies.map((c, idx) => (
              <CopyRow key={c.campaign_id} row={c} rank={idx + 1} />
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}

function CopyRow({ row, rank }) {
  const navigate = useNavigate();
  const isTop = rank === 1;
  const rateBadgeTone = row.book_rate >= 20
    ? "bg-emerald-100 text-emerald-700"
    : row.book_rate >= 10
      ? "bg-amber-100 text-amber-700"
      : row.book_rate > 0
        ? "bg-slate-100 text-slate-600"
        : "bg-slate-100 text-slate-400";
  return (
    <li>
      <button
        type="button"
        onClick={() => navigate(`/ai-outbound/${row.campaign_id}`)}
        data-testid={`ai-outbound-see-copies-row-${row.campaign_id}`}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-50 transition-colors group"
      >
        <div className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold
          ${isTop ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-500"}`}>
          {rank}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="truncate font-medium text-slate-800 text-sm">{row.name}</span>
            {!row.active && (
              <span className="rounded-full bg-slate-100 text-slate-500 px-1.5 py-0.5 text-[9px] font-medium">
                Inactive
              </span>
            )}
          </div>
          <div className="mt-0.5 truncate text-[10px] text-slate-400">
            {row.org_name} · {row.attempts} attempt{row.attempts === 1 ? "" : "s"} · {row.booked} booked
          </div>
        </div>
        <div className="shrink-0 flex items-center gap-1.5">
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold tabular-nums ${rateBadgeTone}`}>
            {row.book_rate}%
          </span>
          <ChevronRight className="h-3.5 w-3.5 text-slate-300 group-hover:text-slate-500" />
        </div>
      </button>
    </li>
  );
}
